import type { Block, BlockType, ShopBlock } from "./blocks";
import type { PortalSchema } from "./portal-schema";

// Walks a page's own blocks plus one level into container blocks (section, iframe's fallback
// blocks) — the same depth PortalContent.tsx's own ad-hoc popup-collection loop already walks.
const pageContainsBlockType = (blocks: Block[], blockType: BlockType): boolean =>
  blocks.some((block) => {
    if (block.type === blockType) return true;
    if (block.type === "section") return block.blocks.some((child) => child.type === blockType);
    if (block.type === "iframe") return block.fallbackBlocks.some((child) => child.type === blockType);
    return false;
  });

/** Whether a block of the given type exists anywhere in the schema — any top-level page, any sub-page, or one level into a container block. */
export const schemaContainsBlockType = (schema: PortalSchema, blockType: BlockType): boolean =>
  schema.pages.some(
    (page) =>
      pageContainsBlockType(page.blocks, blockType) ||
      (page.pages ?? []).some((child) => pageContainsBlockType(child.blocks, blockType))
  );

// Same depth as pageContainsBlockType (a page's own blocks plus one level into section/iframe
// containers), but counting instead of short-circuiting on the first match.
const countBlocksOfTypeInPage = (blocks: Block[], blockType: BlockType): number =>
  blocks.reduce((count, block) => {
    if (block.type === blockType) return count + 1;
    if (block.type === "section") return count + block.blocks.filter((child) => child.type === blockType).length;
    if (block.type === "iframe") {
      return count + block.fallbackBlocks.filter((child) => child.type === blockType).length;
    }
    return count;
  }, 0);

/** Total count of blocks of the given type anywhere in the schema — any top-level page, any sub-page, or one level into a container block. */
export const countBlocksOfType = (schema: PortalSchema, blockType: BlockType): number =>
  schema.pages.reduce((count, page) => {
    const pageCount = countBlocksOfTypeInPage(page.blocks, blockType);
    const childCount = (page.pages ?? []).reduce(
      (sum, child) => sum + countBlocksOfTypeInPage(child.blocks, blockType),
      0
    );
    return count + pageCount + childCount;
  }, 0);

export interface PageContainingBlockType {
  pageId: string;
  slug: string;
}

/** The page (or sub-page) that contains a block of the given type, or null if none does. */
export const findPageContainingBlockType = (
  schema: PortalSchema,
  blockType: BlockType
): PageContainingBlockType | null => {
  for (const page of schema.pages) {
    if (pageContainsBlockType(page.blocks, blockType)) {
      return { pageId: page.id, slug: page.slug };
    }
    const matchingChild = (page.pages ?? []).find((child) => pageContainsBlockType(child.blocks, blockType));
    if (matchingChild) {
      return { pageId: matchingChild.id, slug: matchingChild.slug };
    }
  }
  return null;
};

// Same one-level-into-containers depth as pageContainsBlockType.
const findShopBlockInBlocks = (blocks: Block[]): ShopBlock | undefined => {
  for (const block of blocks) {
    if (block.type === "shop") return block;
    if (block.type === "section") {
      const match = block.blocks.find((child): child is ShopBlock => child.type === "shop");
      if (match) return match;
    }
    if (block.type === "iframe") {
      const match = block.fallbackBlocks.find((child): child is ShopBlock => child.type === "shop");
      if (match) return match;
    }
  }
  return undefined;
};

/**
 * The schema's Shop block, if one exists (a portal can only have one — see portalSchema's
 * superRefine). Unlike findPageContainingBlockType, returns the block itself so callers can read its
 * `products`/`stripeKey` config, not just which page it's on.
 */
export const findShopBlock = (schema: PortalSchema): ShopBlock | undefined => {
  for (const page of schema.pages) {
    const match = findShopBlockInBlocks(page.blocks);
    if (match) return match;
    for (const child of page.pages ?? []) {
      const childMatch = findShopBlockInBlocks(child.blocks);
      if (childMatch) return childMatch;
    }
  }
  return undefined;
};
